import posthog, { type CaptureResult, type PostHogConfig } from 'posthog-js';
import { getPostHogConfig } from './config';

// Allow only analytics metadata. Never send DOM text, attributes, titles,
// referrers, person properties, query strings, or fragments (including OAuth codes).
const allowedProperties = new Set([
  'token', 'distinct_id', '$device_id', '$user_id', '$anon_distinct_id',
  '$session_id', '$window_id', '$is_identified', '$lib', '$lib_version',
  '$browser', '$browser_version', '$os', '$os_version', '$device_type',
  '$viewport_height', '$viewport_width', '$screen_height', '$screen_width',
  '$event_type', '$ce_version', '$sent_at', '$process_person_profile',
]);

export function sanitizeAnalyticsEvent(event: CaptureResult | null): CaptureResult | null {
  if (!event || !['$pageview', '$autocapture', '$identify'].includes(event.event)) return null;
  const properties = Object.fromEntries(
    Object.entries(event.properties).filter(([key]) => allowedProperties.has(key)),
  );
  if (typeof event.properties.$current_url === 'string') {
    try {
      const url = new URL(event.properties.$current_url);
      properties.$current_url = `${url.origin}${url.pathname}`;
      properties.$pathname = url.pathname;
    } catch {
      // Omit malformed URLs rather than forwarding unfiltered values.
    }
  }
  if (Array.isArray(event.properties.$elements)) {
    properties.$elements = event.properties.$elements.map((element: Record<string, unknown>) => ({
      tag_name: element.tag_name,
      nth_child: element.nth_child,
      nth_of_type: element.nth_of_type,
    }));
  }
  return { uuid: event.uuid, event: event.event, timestamp: event.timestamp, properties };
}

export const postHogOptions: Partial<PostHogConfig> = {
  person_profiles: 'identified_only',
  // The provider starts capture after resolving the current session, so a
  // persisted identity from a previous account cannot receive initial events.
  capture_pageview: false,
  capture_pageleave: false,
  autocapture: false,
  mask_all_text: true,
  mask_all_element_attributes: true,
  disable_session_recording: true,
  disable_surveys: true,
  capture_exceptions: false,
  capture_performance: false,
  capture_heatmaps: false,
  capture_dead_clicks: false,
  rageclick: false,
  advanced_disable_flags: true,
  save_campaign_params: false,
  save_referrer: false,
  before_send: sanitizeAnalyticsEvent,
};

export function initializePostHog() {
  const config = getPostHogConfig();
  if (config) posthog.init(config.key, { ...postHogOptions, api_host: config.host });
}
